// src/components/PaymentPage.js
import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader, AlertCircle, User, FileText, CreditCard } from 'lucide-react';


// A mock Button component to make this file runnable on its own.
const Button = ({ onClick, children, className, disabled }) => (
    <button onClick={onClick} className={className} disabled={disabled}>
        {children}
    </button>
);

export default function PaymentPage() {
    const { id: serviceId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [service, setService] = useState(location.state?.service || null);
    const [userData, setUserData] = useState(location.state?.userData || null);
    const [documents, setDocuments] = useState(location.state?.documents || []);
    const [description, setDescription] = useState(location.state?.description || '');
    const [isLoading, setIsLoading] = useState(!location.state?.service);
    const [error, setError] = useState('');

    useEffect(() => {
        const consent = localStorage.getItem('cookieConsent');
        let token;

        if (consent === 'true') {
            token = localStorage.getItem('jwtToken');
        } else {
            token = sessionStorage.getItem('jwtToken');
        }

        // Check authentication
        if (!token) {
            navigate('/login', { state: { from: location.pathname } });
            return;
        }

        // Check if we have the complete flow data
        if (!service || !userData) {
            navigate(`/services/${serviceId}/user-data`);
            return;
        }

        // If service needs to be fetched
        if (service && !service.title) {
            const fetchService = async () => {
                setIsLoading(true);
                try {
                    const res = await axios.get(`https://api.cafpraticheonline.it/api/services/${serviceId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setService({
                        id: res.data.service_id,
                        title: res.data.service_name,
                        price: res.data.price
                    });
                } catch (err) {
                    setError('Impossibile caricare i dettagli del servizio.');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchService();
        }
    }, []); // Empty dependency array - run only once on mount

    const handleProceedToPayment = async () => {
        setIsLoading(true);
        setError('');

        const consent = localStorage.getItem('cookieConsent');
        let token;

        if (consent === 'true') {
            token = localStorage.getItem('jwtToken');
        } else {
            token = sessionStorage.getItem('jwtToken');
        }

        if (!token) {
            setIsLoading(false);
            return;
        }
        console.log("DESCRIPTION", description)

        try {
            // Send all the collected data along with the payment request
            const response = await axios.post(
                `https://api.cafpraticheonline.it/api/services/${serviceId}/purchase`,
                {
                    userData: userData,
                    documentIds: documents.map(doc => doc.id),
                    description:description
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log("Payment response:", response.data);

            if (response.data && response.data.stripe_url) {
                console.log("Redirecting to:", response.data.stripe_url);
                // Validate URL before redirecting
                try {
                    new URL(response.data.stripe_url);
                    window.location.href = response.data.stripe_url;
                } catch (urlError) {
                    console.error("Invalid URL:", response.data.stripe_url);
                    throw new Error("Invalid payment URL received from server");
                }
            } else if (response.data && response.data.url) {
                console.log("Redirecting to (alt):", response.data.url);
                try {
                    new URL(response.data.url);
                    window.location.href = response.data.url;
                } catch (urlError) {
                    console.error("Invalid URL:", response.data.url);
                    throw new Error("Invalid payment URL received from server");
                }
            } else {
                console.error("Response data:", response.data);
                setError("Impossibile ottenere il link di pagamento. Controlla il backend.");
            }
        } catch (err) {
            console.error("Error initiating purchase:", err);
            console.error("Error response:", err.response?.data);

            if (err.response?.status === 401) {
                navigate('/login', { state: { from: location.pathname } });
            } else {
                setError(`Impossibile avviare il pagamento: ${err.response?.data?.message || err.message}`);
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col">
                <Loader className="animate-spin text-yellow-500 h-10 w-10 mb-4" />
                <p className="text-gray-600">Caricamento dei dettagli di pagamento....</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col text-red-600">
                <AlertCircle className="h-10 w-10 mb-4" />
                <p>{error}</p>
            </div>
        );
    }

    if (!service || !userData) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col">
                <AlertCircle className="h-10 w-10 mb-4 text-yellow-500" />
                <p className="text-gray-600 mb-4">Informazioni richieste mancanti</p>
                <Button
                    onClick={() => navigate(`/services/${serviceId}/user-data`)}
                    className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                >
                    Ricomincia
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Order Summary */}
                <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
                    <h1 className="text-2xl font-bold text-gray-800 mb-6">Riepilogo Ordine</h1>

                    {/* Service Details */}
                    <div className="border-b pb-6 mb-6">
                        <div className="flex items-center mb-2">
                            <CreditCard className="h-5 w-5 text-gray-500 mr-2" />
                            <h3 className="text-lg font-semibold text-gray-800">Servizio</h3>
                        </div>
                        <p className="text-gray-700 text-xl font-bold">{service.title}</p>
                        <p className="text-gray-600 text-lg">
                            Prezzo: <span className="font-semibold text-green-600">€{service.price}</span>
                        </p>
                    </div>

                    {/* Customer Information */}
                    <div className="border-b pb-6 mb-6">
                        <div className="flex items-center mb-4">
                            <User className="h-5 w-5 text-gray-500 mr-2" />
                            <h3 className="text-lg font-semibold text-gray-800">Informazioni del Cliente</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="text-gray-600">Nome:</p>
                                <p className="font-medium">{userData.firstName} {userData.lastName}</p>
                            </div>
                            <div>
                                <p className="text-gray-600">Email:</p>
                                <p className="font-medium">{userData.email}</p>
                            </div>
                            <div>
                                <p className="text-gray-600">Telefono:</p>
                                <p className="font-medium">{userData.phone}</p>
                            </div>
                            <div>
                                <p className="text-gray-600">Località:</p>
                                <p className="font-medium">{userData.city}, {userData.country}</p>
                            </div>
                        </div>
                    </div>

                    {/* Documents Uploaded */}
                    <div className="mb-6">
                        <div className="flex items-center mb-4">
                            <FileText className="h-5 w-5 text-gray-500 mr-2" />
                            <h3 className="text-lg font-semibold text-gray-800">Documenti Caricati</h3>
                        </div>
                        {documents.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {documents.map((doc, index) => (
                                    <div key={doc.id || index} className="flex items-center bg-gray-50 p-2 rounded">
                                        <FileText className="h-4 w-4 text-green-500 mr-2" />
                                        <span className="text-sm text-gray-700 truncate">{doc.name}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-sm">Nessun documento caricato</p>
                        )}
                    </div>

                    {/* Total */}
                    <div className="border-t pt-6">
                        <div className="flex justify-between items-center text-xl font-bold">
                            <span>Importo Totale:</span>
                            <span className="text-green-600">€{service.price}</span>
                        </div>
                    </div>
                </div>

                {/* Payment Action */}
                <div className="bg-white rounded-2xl shadow-lg p-8">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Pagamento</h2>
                    <p className="text-gray-600 mb-6">
                        Verrai reindirizzato al nostro processore di pagamento sicuro per completare l’acquisto.
                    </p>

                    <div className="flex gap-4">
                        <Button
                            onClick={() => navigate(`/services/${serviceId}/documents`, {
                                state: { service, userData }
                            })}
                            className="flex-1 py-3 rounded-lg font-bold text-lg transition-all duration-200
                                            bg-gray-200 text-gray-700 hover:bg-gray-300"
                        >
                            Torna ai Documenti
                        </Button>

                        <Button
                            onClick={handleProceedToPayment}
                            disabled={isLoading}
                            className="flex-2 py-3 px-8 rounded-lg font-bold text-lg transition-all duration-200
                                            bg-gradient-to-r from-[#F2C231] to-[#C69A32] text-white hover:from-[#C69A32] hover:to-[#F2C231]
                                            disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? 'Elaborazione...' : 'Paga Ora'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}